/** @file Pixel positions of a mesh attachment's vertices (geometry's mesh/skin vertices, then the camera). */
import { meshVertices } from '@pose2d/geometry/meshVertices';
import { skinVertices } from '@pose2d/geometry/skinVertices';
import { slotWorld } from '@pose2d/geometry/slotWorld';
import type { Camera } from '@pose2d/geometry/types';
import type { OverlayInput } from '../overlayInput';
import { meshScratch, type MeshBuffers } from './meshScratch';

/** Scratch slot world for rigid meshes, filled by geometry's slotWorld (W2-21). */
const world = new Float64Array(6);
/** The camera's worldToPixel of the current call (a missing entry reads 0, as in affine.apply). */
const M: [number, number, number, number, number, number] = [0, 0, 0, 0, 0, 0];

/**
 * Maps b.local[0, n) through the camera into b.px[0, n) and makes px[n, pxUsed) NaN. Only [0, n) of `local` is read,
 * like the n-long arrays this used to allocate per mesh. For an odd n the last vertex has no y, and its x pixel was
 * a * x + b * NaN + e: NaN.
 */
function toPixels(camera: Camera, n: number, buffers: MeshBuffers): void {
  // affine.apply's arithmetic, term for term, inlined, over an integer pair index: no number is boxed per vertex.
  for (let k = 0; k < 6; k++) M[k] = camera.worldToPixel[k] ?? 0;
  const a = M[0], b = M[1], c = M[2], d = M[3], e = M[4], f = M[5];
  const local = buffers.local, px = buffers.px;
  let i = 0;
  for (; i + 1 < n; i += 2) {
    const x = local[i] ?? NaN, y = local[i + 1] ?? NaN;
    px[i] = a * x + b * y + e;
    px[i + 1] = c * x + d * y + f;
  }
  if (i < n) px[i] = NaN;
  if (buffers.pxUsed > n) px.fill(NaN, n, buffers.pxUsed);
  buffers.pxUsed = n;
}

/**
 * [x, y] per vertex in pixels for mesh attachment `att` shown by `slot` (rigid: slotWorld, mount included;
 * skinned: influence bone worlds only), with `pose.deform[att]` applied when non-empty (W2-03); null when `att`
 * is not a mesh. The array is meshScratch's, valid until the next call: its first `uvs.length` entries are the mesh's
 * and every later one reads NaN, as a read past an array of exactly that length gave (no array per mesh).
 */
export function meshPixels(input: OverlayInput, slot: number, att: number): Float64Array | null {
  const mesh = input.data.attachments[att];
  if (mesh?.type !== 'mesh') return null;
  const n = mesh.uvs.length, buffers = meshScratch.forMesh(n, mesh.triangles.length);
  buffers.local.fill(0, 0, n);
  const d = input.pose.deform[att];
  const deform = d !== undefined && d.length > 0 ? d : null;
  if (mesh.skin.kind === 'rigid') {
    // slotWorld fills `world` in place; pass the Float64Array itself (its return type is the wider Affine).
    slotWorld(input.pose, input.data, slot, world);
    meshVertices(buffers.local, 0, mesh, world, 0, deform);
  } else {
    skinVertices(buffers.local, 0, mesh, input.pose.world, deform);
  }
  toPixels(input.camera, n, buffers);
  return buffers.px;
}
